/**
 * The client <-> battle-server contract: ports, room names and message shapes.
 * Both sides import from here so a rename breaks the build rather than the game.
 */

/**
 * Fixed dev ports. Every service pins its port with strictPort / a hard bind and
 * the `free-port` script kills stale holders, so these numbers are reliable
 * enough to hard-code in dev URLs.
 */
export const DEV_PORTS = {
  gameClient: 5220,
  battleServer: 5221,
  adminEditor: 5222,
} as const;

export const DEFAULT_BATTLE_SERVER_URL = `ws://localhost:${DEV_PORTS.battleServer}`;

/** Matchmaking name the battle room is registered under. */
export const BATTLE_ROOM = 'battle';

/** Server simulation rate. */
export const TICK_RATE = 20;

export type BattlePhase = 'lobby' | 'running' | 'cleared' | 'failed';

/**
 * How a character is carried over the wire.
 *
 * Deliberately strings rather than structured fields: this is sent once on join and
 * never changes during a run, so a compact encoding beats 28 schema fields per player.
 * `encodeAppearance` / `decodeAppearance` are the only places that know the format.
 */
export interface CharacterWire {
  gender: string;
  stance: string;
  packId: string;
  /** Catalogue variant ids, comma separated. */
  appearance: string;
  /** `region=#rrggbb` pairs, comma separated. */
  tints: string;
  colourway: string;
}

export function encodeAppearance(variantIds: string[]): string {
  return variantIds.join(',');
}

export function decodeAppearance(encoded: string): string[] {
  return encoded ? encoded.split(',').filter(Boolean) : [];
}

export function encodeTints(tints: Record<string, string>): string {
  return Object.entries(tints)
    .map(([region, hex]) => `${region}=${hex}`)
    .join(',');
}

export function decodeTints(encoded: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of encoded.split(',')) {
    const [region, hex] = pair.split('=');
    if (region && hex) out[region] = hex;
  }
  return out;
}

/** Options accepted by `client.joinOrCreate(BATTLE_ROOM, ...)`. */
export interface BattleJoinOptions {
  /** Which authored dungeon to play. */
  levelId?: string;
  displayName?: string;
  /** How this player looks and fights. Absent means the server's defaults. */
  character?: Partial<CharacterWire>;
}

/** client -> server */
export const ClientMessage = {
  /** Steering intent, not a position: the server stays authoritative. */
  Move: 'move',
  Interact: 'interact',
  Ready: 'ready',
} as const;
export type ClientMessage = (typeof ClientMessage)[keyof typeof ClientMessage];

/** server -> client */
export const ServerMessage = {
  Welcome: 'welcome',
  PhaseChanged: 'phase',
} as const;
export type ServerMessage = (typeof ServerMessage)[keyof typeof ServerMessage];

export interface MovePayload {
  /** Normalised direction on the ground plane, -1..1. */
  dx: number;
  dz: number;
  /** Facing in radians. */
  yaw: number;
}

export interface InteractPayload {
  tileX: number;
  tileZ: number;
}

export interface WelcomePayload {
  sessionId: string;
  levelId: string;
  tickRate: number;
}

export interface PhaseChangedPayload {
  phase: BattlePhase;
}
